const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const deepMerge = (base, override) => {
  if (!override) return base;

  const result = {...base};

  Object.keys(override).forEach((key) => {
    const overrideVal = override[key];
    const baseVal = base[key];

    if (isPlainObject(overrideVal) && isPlainObject(baseVal)) {
      result[key] = deepMerge(baseVal, overrideVal);
    } else if (overrideVal !== undefined && overrideVal !== null && overrideVal !== '') {
      result[key] = overrideVal;
    }
  });

  return result;
};

/**
 * ClientTheme/PartnerTheme hardcode the literal font-family string on every
 * `typography.styles.*` variant (display/heading/body/cta/label/express)
 * rather than referencing `typography.fontFamily.primary`. So a fontFamily
 * override needs to cascade into those nested literals too, or text
 * rendered via `theme.typography.styles.*.fontFamily` would silently keep
 * the old font. Walks any object tree and replaces every `fontFamily`
 * string leaf with `newPrimaryFontFamily`.
 */
const cascadeFontFamily = (node, newPrimaryFontFamily) => {
  if (!isPlainObject(node)) return node;

  const result = {};
  Object.keys(node).forEach((key) => {
    const value = node[key];
    if (key === 'fontFamily' && typeof value === 'string') {
      result[key] = newPrimaryFontFamily;
    } else if (isPlainObject(value)) {
      result[key] = cascadeFontFamily(value, newPrimaryFontFamily);
    } else {
      result[key] = value;
    }
  });
  return result;
};

/**
 * Overlays a fetched white-label config onto a base named theme
 * (clientTheme/partnerTheme). Only colors and font-family are overridden -
 * spacing/sizing/weights/layout tokens stay untouched. Any field missing
 * from `wlConfig` falls through to the base theme's existing value.
 *
 * @param {object} baseNamedTheme - clientTheme or partnerTheme
 * @param {object|null} wlConfig - fetched white-label config, or null
 * @returns {object} theme object, with an added `whiteLabel` asset key
 */
export const mergeWhiteLabelTheme = (baseNamedTheme, wlConfig) => {
  if (!wlConfig) return baseNamedTheme;

  let merged = deepMerge(baseNamedTheme, {
    colors: wlConfig.colors,
    typography: wlConfig.typography?.fontFamily
      ? {fontFamily: wlConfig.typography.fontFamily}
      : undefined,
  });

  const newPrimaryFontFamily = wlConfig.typography?.fontFamily?.primary;
  if (newPrimaryFontFamily && merged.typography?.styles) {
    merged = {
      ...merged,
      typography: {
        ...merged.typography,
        styles: cascadeFontFamily(merged.typography.styles, newPrimaryFontFamily),
      },
    };
  }

  return {
    ...merged,
    whiteLabel: {
      logoUrl: wlConfig.assets?.logoUrl,
      faviconUrl: wlConfig.assets?.faviconUrl,
      pageTitle: wlConfig.meta?.pageTitle,
      // A theme can be "white-labelled" with only a name/logo and no color
      // overrides (current hydra-go integration never sends `colors`).
      // Callers that derive backgrounds/accents from theme.colors.brand.primary
      // (sidebar gradient, step rings, pot colors, etc.) must gate on this,
      // not on `whiteLabel` truthiness alone, or they'll "derive" from a
      // primary color that's still just the Wealthy default.
      hasCustomColors: Boolean(wlConfig.colors),
    },
  };
};
